/**
 * Mapper de productos — Portal Cliente Menuee
 *
 * Responsabilidad: transformar filas del catálogo 3 capas (productos_sucursal +
 * productos_maestro + categorias_producto) a objetos de vista para componentes.
 *
 * Consume: filas crudas devueltas por productosService (PostgREST).
 *
 * Qué nunca debe hacer:
 * - Consultar Supabase directamente.
 * - Calcular precios finales del pedido ni totales con delivery.
 * - Contener lógica visual ni JSX.
 *
 * Flujo: ProductoCard ← mapProductoToView() ← productosService ← Supabase
 */

/**
 * @typedef {Object} ProductoLegacy
 * @property {string} id - productos_sucursal.id (producto_sucursal_id en E6).
 * @property {string} nombre
 * @property {string} [descripcion]
 * @property {number} precio
 * @property {string} categoria
 * @property {string|null} imagen_url
 * @property {string} [img]
 * @property {string} [productoMaestroId]
 * @property {string} [categoriaId]
 * @property {number} [categoriaOrden]
 * @property {boolean} [activo]
 * @property {boolean} [visible]
 * @property {boolean} [esExtra]
 * @property {boolean} [armable]
 * @property {number} [armable_cantidad]
 * @property {string|null} [lista_armable_id]
 * @property {boolean} [mostrar_extras]
 * @property {boolean} [mostrar_categoria_extras]
 * @property {{ id: string, nombre: string, precio: number, orden: number }[]} [ingredientes]
 * @property {boolean} [ingredientes_con_precio]
 * @property {boolean} [con_tamanos]
 * @property {{ id: string, nombre: string, precio: number, orden: number }[]} [tamanos]
 */

/**
 * @param {*} value
 * @returns {Object|null}
 */
function unwrapJoin(value) {
  if (Array.isArray(value)) {
    return value[0] ?? null;
  }
  return value ?? null;
}

/**
 * @param {string|null|undefined} nombre
 * @returns {string}
 */
export function normalizarNombreCategoria(nombre) {
  return String(nombre || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
}

/**
 * @param {Object|null|undefined} categoria
 * @returns {boolean}
 */
export function isCategoriaExtra(categoria) {
  if (!categoria) {
    return false;
  }
  if (categoria.es_extra === true) {
    return true;
  }
  return normalizarNombreCategoria(categoria.nombre) === "extras";
}

/**
 * @param {Object|null|undefined} row - fila cruda productos_sucursal
 * @returns {boolean}
 */
export function isProductoExtraFromRow(row) {
  const maestro = unwrapJoin(row?.producto);
  const categoria = unwrapJoin(maestro?.categoria);
  return isCategoriaExtra(categoria);
}

/**
 * @param {ProductoLegacy|null|undefined} producto
 * @returns {boolean}
 */
export function isProductoExtra(producto) {
  if (!producto) {
    return false;
  }
  if (producto.esExtra === true) {
    return true;
  }
  return normalizarNombreCategoria(producto.categoria) === "extras";
}

export const PREFIJO_COMENTARIO_ARMADO = "Armado: ";
export const PREFIJO_COMENTARIO_TAMANO = "Tamaño: ";
export const SEPARADOR_COMENTARIO_OPCIONES = " · ";

/**
 * @param {*} rawIngredientes
 * @returns {{ id: string, nombre: string, orden: number }[]}
 */
export function mapIngredientesActivos(rawIngredientes) {
  const lista = Array.isArray(rawIngredientes) ? rawIngredientes : [];

  return lista
    .filter((ing) => ing && ing.activo !== false)
    .map((ing) => {
      const nombre = String(ing.nombre || "").trim();
      const ordenRaw = Number(ing.orden);
      const precioRaw = Number(ing.precio);
      return {
        id: ing.id != null ? String(ing.id) : "",
        nombre,
        precio: Number.isFinite(precioRaw) && precioRaw > 0 ? precioRaw : 0,
        orden: Number.isFinite(ordenRaw) ? ordenRaw : 0,
      };
    })
    .filter((ing) => ing.id && ing.nombre)
    .sort((a, b) => {
      if (a.orden !== b.orden) {
        return a.orden - b.orden;
      }
      return a.nombre.localeCompare(b.nombre, "es");
    });
}

/**
 * Ingredientes de lista reutilizable; si no hay, los del producto.
 *
 * @param {Object|null|undefined} maestro
 * @returns {{ id: string, nombre: string, orden: number }[]}
 */
export function resolverIngredientesMaestro(maestro) {
  const listaArmable = unwrapJoin(maestro?.lista_armable);
  const deLista = mapIngredientesActivos(listaArmable?.ingredientes);
  if (deLista.length > 0) {
    return {
      ingredientes: deLista,
      ingredientes_con_precio: listaArmable?.ingredientes_con_precio === true,
      lista_armable_id: listaArmable?.id
        ? String(listaArmable.id)
        : maestro?.lista_armable_id
          ? String(maestro.lista_armable_id)
          : null,
    };
  }
  return {
    ingredientes: mapIngredientesActivos(maestro?.ingredientes),
    ingredientes_con_precio: false,
    lista_armable_id: maestro?.lista_armable_id
      ? String(maestro.lista_armable_id)
      : null,
  };
}

/**
 * @param {*} rawTamanos
 * @returns {{ id: string, nombre: string, precio: number, orden: number }[]}
 */
export function mapTamanosActivos(rawTamanos) {
  const lista = Array.isArray(rawTamanos) ? rawTamanos : [];

  return lista
    .filter((tamano) => tamano && tamano.activo !== false)
    .map((tamano) => {
      const nombre = String(tamano.nombre || "").trim();
      const precio = Number(tamano.precio);
      const ordenRaw = Number(tamano.orden);
      return {
        id: tamano.id != null ? String(tamano.id) : "",
        nombre,
        precio,
        orden: Number.isFinite(ordenRaw) ? ordenRaw : 0,
      };
    })
    .filter(
      (tamano) => tamano.id && tamano.nombre && Number.isFinite(tamano.precio)
    )
    .sort((a, b) => {
      if (a.orden !== b.orden) {
        return a.orden - b.orden;
      }
      if (a.precio !== b.precio) {
        return a.precio - b.precio;
      }
      return a.nombre.localeCompare(b.nombre, "es");
    });
}

/**
 * @param {Object|null|undefined} maestro
 * @param {*} precioSucursal
 * @returns {{ con_tamanos: boolean, tamanos: { id: string, nombre: string, precio: number, orden: number }[], precio: number }}
 */
export function resolverCamposTamanos(maestro, precioSucursal) {
  const tamanos = mapTamanosActivos(maestro?.tamanos);
  if (maestro?.con_tamanos === true && tamanos.length >= 1) {
    return {
      con_tamanos: true,
      tamanos,
      precio: Math.min(...tamanos.map((tamano) => tamano.precio)),
    };
  }

  const precio = Number(precioSucursal);
  return {
    con_tamanos: false,
    tamanos: [],
    precio: Number.isFinite(precio) ? precio : NaN,
  };
}

/**
 * Un producto es armable si el flag es true y hay al menos 1 ingrediente activo.
 * armable_cantidad es el máximo de unidades (1–20), no una cantidad exacta.
 *
 * @param {Object|null|undefined} maestro
 * @returns {{ armable: boolean, armable_cantidad: number, ingredientes: { id: string, nombre: string, orden: number }[], lista_armable_id: string|null }}
 */
export function resolverCamposArmable(maestro) {
  const {
    ingredientes,
    ingredientes_con_precio,
    lista_armable_id,
  } = resolverIngredientesMaestro(maestro);

  if (maestro?.armable !== true || ingredientes.length < 1) {
    return {
      armable: false,
      armable_cantidad: 0,
      ingredientes: [],
      ingredientes_con_precio: false,
      lista_armable_id,
    };
  }

  const cantidadRaw = Number(maestro.armable_cantidad);
  const cantidadConfig = Number.isFinite(cantidadRaw) ? Math.floor(cantidadRaw) : 0;
  const cantidadMaxima = Math.min(20, Math.max(0, cantidadConfig));

  if (cantidadMaxima < 1) {
    return {
      armable: false,
      armable_cantidad: 0,
      ingredientes: [],
      ingredientes_con_precio: false,
      lista_armable_id,
    };
  }

  return {
    armable: true,
    armable_cantidad: cantidadMaxima,
    ingredientes,
    ingredientes_con_precio,
    lista_armable_id,
  };
}

/**
 * @param {ProductoLegacy|null|undefined} producto
 * @returns {boolean}
 */
export function isProductoArmable(producto) {
  return (
    producto?.armable === true &&
    Number(producto?.armable_cantidad) >= 1 &&
    Array.isArray(producto?.ingredientes) &&
    producto.ingredientes.length > 0
  );
}

/**
 * La sección de extras solo aparece si el producto lo permite y tiene
 * una lista de ingredientes válida. Los tamaños no dependen de este flag.
 *
 * @param {ProductoLegacy|null|undefined} producto
 * @returns {boolean}
 */
export function muestraSeccionExtras(producto) {
  return producto?.mostrar_extras === true && isProductoArmable(producto);
}

/**
 * Ofrece productos de la categoría Extras. No depende de mostrar_extras ni del armado.
 *
 * @param {ProductoLegacy|null|undefined} producto
 * @returns {boolean}
 */
export function muestraCategoriaExtras(producto) {
  return producto?.mostrar_categoria_extras === true;
}

/**
 * @param {ProductoLegacy|null|undefined} producto
 * @returns {boolean}
 */
export function isProductoConTamanos(producto) {
  return (
    producto?.con_tamanos === true &&
    Array.isArray(producto?.tamanos) &&
    producto.tamanos.length > 0
  );
}

/**
 * @param {ProductoLegacy|null|undefined} producto
 * @returns {boolean}
 */
export function resolverTamanoElegido(producto, tamanoOpcion) {
  if (!isProductoConTamanos(producto)) {
    return null;
  }
  const lista = producto.tamanos || [];
  if (!tamanoOpcion) {
    return null;
  }
  if (tamanoOpcion.id != null && String(tamanoOpcion.id) !== "") {
    const porId = lista.find((tamano) => String(tamano.id) === String(tamanoOpcion.id));
    if (porId) {
      return porId;
    }
  }
  const nombre = String(tamanoOpcion.nombre || "").trim();
  if (!nombre) {
    return null;
  }
  return (
    lista.find((tamano) => tamano.nombre === nombre) ||
    lista.find(
      (tamano) => tamano.nombre.toLowerCase() === nombre.toLowerCase()
    ) ||
    null
  );
}

/**
 * @param {string|null|undefined} nombreProducto
 * @param {string|null|undefined} tamanoNombre
 * @returns {string}
 */
export function formatearNombreConTamano(nombreProducto, tamanoNombre) {
  const base = String(nombreProducto || "").trim();
  const tamano = String(tamanoNombre || "").trim();
  if (!base) {
    return tamano ? `(${tamano})` : "";
  }
  if (!tamano) {
    return base;
  }
  return `${base} (${tamano})`;
}

/**
 * @param {ProductoLegacy|null|undefined} producto
 * @returns {boolean}
 */
export function productoRequiereSelector(producto) {
  return muestraSeccionExtras(producto) || isProductoConTamanos(producto);
}

/**
 * @param {{ nombre?: string, cantidad?: number }[]|string[]} ingredientesElegidos
 * @returns {{ nombre: string, cantidad: number }[]}
 */
export function agruparIngredientesArmado(ingredientesElegidos) {
  const grupos = [];

  for (const ing of ingredientesElegidos || []) {
    const nombre = String(
      typeof ing === "string" ? ing : ing?.nombre || ""
    ).trim();
    if (!nombre) {
      continue;
    }
    const extra = Number(typeof ing === "object" ? ing?.cantidad : NaN);
    const cantidad =
      Number.isFinite(extra) && extra > 0 ? Math.floor(extra) : 1;
    const existente = grupos.find(
      (grupo) => grupo.nombre.toLowerCase() === nombre.toLowerCase()
    );
    if (existente) {
      existente.cantidad += cantidad;
    } else {
      grupos.push({ nombre, cantidad });
    }
  }

  return grupos.filter((grupo) => grupo.cantidad > 0);
}

/**
 * Precio base: tamaño elegido o precio del producto.
 *
 * @param {ProductoLegacy|null|undefined} producto
 * @param {{ precio?: number }|null|undefined} tamano
 * @returns {number}
 */
export function calcularPrecioBaseProducto(producto, tamano = null) {
  const precioTamano = Number(tamano?.precio);
  if (Number.isFinite(precioTamano)) {
    return precioTamano;
  }
  const precioProducto = Number(producto?.precio);
  return Number.isFinite(precioProducto) ? precioProducto : 0;
}

/**
 * Suma ingredientes solo si la lista está marcada ingredientes_con_precio.
 *
 * @param {ProductoLegacy|null|undefined} producto
 * @param {{ nombre?: string, cantidad?: number }[]|string[]} ingredientesElegidos
 * @returns {number}
 */
export function calcularSumaIngredientesArmado(producto, ingredientesElegidos = []) {
  if (producto?.ingredientes_con_precio !== true) {
    return 0;
  }
  const catalogo = producto.ingredientes || [];
  let suma = 0;
  for (const grupo of agruparIngredientesArmado(ingredientesElegidos)) {
    const ing =
      catalogo.find((item) => item.nombre === grupo.nombre) ||
      catalogo.find(
        (item) => item.nombre.toLowerCase() === grupo.nombre.toLowerCase()
      );
    const unitario = Number(ing?.precio);
    const precioUnitario =
      Number.isFinite(unitario) && unitario > 0 ? unitario : 0;
    suma += precioUnitario * grupo.cantidad;
  }
  return suma;
}

/**
 * Total unitario: base + ingredientes (si suman).
 *
 * @param {ProductoLegacy|null|undefined} producto
 * @param {{ tamano?: { precio?: number }|null, ingredientesElegidos?: { nombre?: string, cantidad?: number }[]|string[] }} [opciones]
 * @returns {number}
 */
export function calcularPrecioItemArmable(
  producto,
  { tamano = null, ingredientesElegidos = [] } = {}
) {
  return (
    calcularPrecioBaseProducto(producto, tamano) +
    calcularSumaIngredientesArmado(producto, ingredientesElegidos)
  );
}

/**
 * @param {{ nombre?: string, cantidad?: number }[]|string[]} ingredientesElegidos
 * @returns {string}
 */
export function formatearComentarioArmado(ingredientesElegidos) {
  const grupos = agruparIngredientesArmado(ingredientesElegidos);
  if (!grupos.length) {
    return "";
  }
  const texto = grupos
    .map((grupo) =>
      grupo.cantidad > 1 ? `${grupo.nombre} x${grupo.cantidad}` : grupo.nombre
    )
    .join(", ");
  return `${PREFIJO_COMENTARIO_ARMADO}${texto}`;
}

/**
 * @param {string|null|undefined} token
 * @returns {{ nombre: string, cantidad: number }|null}
 */
function parsearTokenArmado(token) {
  const texto = String(token || "").trim();
  if (!texto) {
    return null;
  }
  const match = texto.match(/^(.*?)(?:\s+x(\d+))$/i);
  if (match) {
    const nombre = match[1].trim();
    const cantidad = Number(match[2]);
    if (!nombre || !Number.isFinite(cantidad) || cantidad < 1) {
      return null;
    }
    return { nombre, cantidad: Math.floor(cantidad) };
  }
  return { nombre: texto, cantidad: 1 };
}

/**
 * @param {string|null|undefined} parte
 * @returns {{ nombre: string, cantidad: number }[]}
 */
function extraerArmadoAgrupadoDeParte(parte) {
  const texto = String(parte || "").trim();
  if (!texto.toLowerCase().startsWith("armado:")) {
    return [];
  }
  const resto = texto.slice(texto.indexOf(":") + 1).trim();
  if (!resto) {
    return [];
  }
  return resto
    .split(",")
    .map((token) => parsearTokenArmado(token))
    .filter(Boolean);
}

/**
 * @param {string|null|undefined} comentario
 * @returns {{ tamano: string|null, armado: { nombre: string, cantidad: number }[] }}
 */
export function parsearComentarioOpciones(comentario) {
  const texto = String(comentario || "").trim();
  if (!texto) {
    return { tamano: null, armado: [] };
  }

  const partes = texto
    .split(/\s*[·|]\s*/)
    .map((parte) => parte.trim())
    .filter(Boolean);

  let tamano = null;
  let armado = [];

  for (const parte of partes) {
    const lower = parte.toLowerCase();
    if (lower.startsWith("tamaño:") || lower.startsWith("tamano:")) {
      tamano = parte.slice(parte.indexOf(":") + 1).trim() || null;
    } else if (lower.startsWith("armado:")) {
      armado = extraerArmadoAgrupadoDeParte(parte);
    }
  }

  if (!tamano && armado.length === 0) {
    armado = extraerArmadoAgrupadoDeParte(texto);
  }

  return { tamano, armado };
}

/**
 * @param {string|null|undefined} comentario
 * @returns {string[]}
 */
export function parsearNombresArmado(comentario) {
  const nombres = [];
  for (const grupo of parsearComentarioOpciones(comentario).armado) {
    for (let i = 0; i < grupo.cantidad; i += 1) {
      nombres.push(grupo.nombre);
    }
  }
  return nombres;
}

/**
 * @param {Object} [params]
 * @param {string|null} [params.tamanoNombre]
 * @param {{ nombre?: string, cantidad?: number }[]|string[]} [params.ingredientesElegidos]
 * @returns {string|null}
 */
export function formatearComentarioOpciones({
  tamanoNombre = null,
  ingredientesElegidos = [],
} = {}) {
  const partes = [];
  const nombreTamano = String(tamanoNombre || "").trim();
  if (nombreTamano) {
    partes.push(`${PREFIJO_COMENTARIO_TAMANO}${nombreTamano}`);
  }
  const armado = formatearComentarioArmado(ingredientesElegidos);
  if (armado) {
    partes.push(armado);
  }
  return partes.length ? partes.join(SEPARADOR_COMENTARIO_OPCIONES) : null;
}

/**
 * Vacío es válido (0 ingredientes). Si hay armado, no puede superar el máximo.
 *
 * @param {string|null|undefined} comentario
 * @param {number} [cantidadMaxima]
 * @returns {boolean}
 */
export function esComentarioArmadoValido(comentario, cantidadMaxima) {
  const unidades = parsearNombresArmado(comentario).length;
  if (cantidadMaxima == null) {
    return true;
  }
  const n = Number(cantidadMaxima);
  if (!Number.isFinite(n) || n < 0) {
    return unidades === 0;
  }
  return unidades <= n;
}

/**
 * @param {import('../hooks/useCarrito').CarritoItem[]|null|undefined} carrito
 * @returns {string|null} mensaje de error o null si está ok
 */
export function validarCarritoOpciones(carrito) {
  for (const item of carrito || []) {
    const nombre = item?.nombreBase || item?.nombre || "tu producto";
    if (item?.con_tamanos) {
      const { tamano } = parsearComentarioOpciones(item.comentario);
      if (!tamano) {
        return `Elige el tamaño de ${nombre}`;
      }
    }
    if (
      item?.armable &&
      !esComentarioArmadoValido(item.comentario, item.armable_cantidad)
    ) {
      return `Elige como máximo ${item.armable_cantidad} ingredientes en ${nombre}`;
    }
  }
  return null;
}

/**
 * @param {import('../hooks/useCarrito').CarritoItem[]|null|undefined} carrito
 * @returns {string|null}
 */
export function validarCarritoArmables(carrito) {
  return validarCarritoOpciones(carrito);
}

/**
 * Mapea una fila cruda de productos_sucursal (con joins) a ProductoLegacy.
 *
 * @param {Object|null|undefined} row
 * @returns {ProductoLegacy|null}
 */
export function mapProductoToView(row) {
  if (!row || row.activo === false || row.visible === false) {
    return null;
  }

  const maestro = unwrapJoin(row.producto);
  if (!maestro || maestro.activo === false) {
    return null;
  }

  const categoria = unwrapJoin(maestro.categoria);
  const nombre = String(maestro.nombre || "").trim();
  if (!nombre) {
    return null;
  }

  const imagenUrlRaw =
    maestro.imagen_url ?? maestro.imagen ?? maestro.img ?? null;
  const imagen_url =
    imagenUrlRaw != null && String(imagenUrlRaw).trim() !== ""
      ? String(imagenUrlRaw).trim()
      : null;

  const armableCampos = resolverCamposArmable(maestro);
  const tamanosCampos = resolverCamposTamanos(maestro, row.precio);
  if (!Number.isFinite(tamanosCampos.precio)) {
    return null;
  }

  return {
    id: row.id,
    productoSucursalId: row.id,
    productoMaestroId: maestro.id,
    nombre,
    descripcion: maestro.descripcion ? String(maestro.descripcion) : "",
    precio: tamanosCampos.precio,
    categoria: categoria?.nombre ? String(categoria.nombre) : "",
    categoriaId: categoria?.id ?? null,
    categoriaOrden: Number.isFinite(Number(categoria?.orden))
      ? Number(categoria.orden)
      : 0,
    imagen_url,
    img: imagen_url ?? undefined,
    activo: true,
    visible: true,
    esExtra: isCategoriaExtra(categoria),
    armable: armableCampos.armable,
    armable_cantidad: armableCampos.armable_cantidad,
    lista_armable_id: armableCampos.lista_armable_id,
    mostrar_extras: maestro.mostrar_extras === true,
    mostrar_categoria_extras: maestro.mostrar_categoria_extras === true,
    ingredientes: armableCampos.ingredientes,
    ingredientes_con_precio: armableCampos.ingredientes_con_precio === true,
    con_tamanos: tamanosCampos.con_tamanos,
    tamanos: tamanosCampos.tamanos,
  };
}

/**
 * @param {ProductoLegacy|null|undefined} producto
 * @returns {string|null}
 */
export function resolverProductoSucursalId(producto) {
  const id = producto?.productoSucursalId || producto?.id;
  return id ? String(id) : null;
}

/**
 * @param {Object[]} rows
 * @returns {ProductoLegacy[]}
 */
export function mapProductosToView(rows) {
  return (rows || []).map(mapProductoToView).filter(Boolean);
}

/**
 * Productos de categoría extra (es_extra o nombre "Extras") para ModalProducto.
 *
 * @param {Object[]} rows - filas crudas productos_sucursal
 * @returns {ProductoLegacy[]}
 */
export function mapProductosToExtras(rows) {
  return mapProductosToView((rows || []).filter(isProductoExtraFromRow)).filter(
    (producto) => !productoRequiereSelector(producto)
  );
}

/**
 * Excluye extras del listado principal de la carta.
 *
 * @param {ProductoLegacy[]} productos
 * @returns {ProductoLegacy[]}
 */
export function filterProductosParaCarta(productos) {
  return (productos || []).filter((producto) => !isProductoExtra(producto));
}

/**
 * Orden de carta: categoría (orden, nombre) y luego producto por nombre.
 *
 * @param {ProductoLegacy[]} productos
 * @returns {ProductoLegacy[]}
 */
export function sortProductosForCarta(productos) {
  return [...(productos || [])].sort((a, b) => {
    const ordenA = a.categoriaOrden ?? 0;
    const ordenB = b.categoriaOrden ?? 0;
    if (ordenA !== ordenB) {
      return ordenA - ordenB;
    }

    const cmpCategoria = (a.categoria || "").localeCompare(
      b.categoria || "",
      "es"
    );
    if (cmpCategoria !== 0) {
      return cmpCategoria;
    }

    return (a.nombre || "").localeCompare(b.nombre || "", "es");
  });
}
